import React, { useState } from 'react'
import { useQuery } from 'react-query'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'

import { formatDateTime } from '@/lib/dateUtils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { StatCard, StatCardGroup } from '@/components/shared'
import { emailCampaignService } from '@/services/emailCampaignService'
import EmailPreview from '@/components/MarketingEmail/EmailPreview'

// Fixed categorical order (never cycled) — one hue per engagement metric
const METRIC_COLORS = {
  delivered: '#2a78d6', // blue
  opened: '#1baf7a',    // aqua
  clicked: '#eda100',   // yellow
  bounced: '#e34948',   // red
  unsubscribed: '#4a3aa7', // violet
}

export const OverviewTab = ({ campaignId }) => {
  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    ['campaign-summary', campaignId], () => emailCampaignService.getSummary(campaignId)
  )
  const { data: seriesData, isLoading: seriesLoading } = useQuery(
    ['campaign-timeseries', campaignId], () => emailCampaignService.getTimeseries(campaignId)
  )

  const summary = summaryData?.data
  const series = (seriesData?.data?.series || []).map((row) => ({
    ...row,
    label: new Date(row.period).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: seriesData?.data?.bucket === 'hour' ? 'numeric' : undefined }),
  }))

  if (summaryLoading) return <Skeleton className="h-64 w-full" />

  const funnel = [
    { label: 'Sent', value: summary?.counts.sent || 0 },
    { label: 'Delivered', value: summary?.counts.delivered || 0 },
    { label: 'Opened', value: summary?.counts.opened || 0 },
    { label: 'Clicked', value: summary?.counts.clicked || 0 },
  ]

  return (
    <div className="space-y-6">
      <StatCardGroup className="lg:grid-cols-5">
        <StatCard title="Delivery Rate" value={`${summary?.rates.deliveryRate ?? 0}%`} contentClassName="p-4" />
        <StatCard
          title="Open Rate"
          value={`${summary?.rates.openRate ?? 0}%`}
          contentClassName="p-4"
          description={summary?.health.openRate}
        />
        <StatCard
          title="Click Rate"
          value={`${summary?.rates.clickRate ?? 0}%`}
          contentClassName="p-4"
          description={summary?.health.clickRate}
        />
        <StatCard title="Bounce Rate" value={`${summary?.rates.bounceRate ?? 0}%`} contentClassName="p-4" />
        <StatCard title="Unsubscribe Rate" value={`${summary?.rates.unsubscribeRate ?? 0}%`} contentClassName="p-4" />
      </StatCardGroup>

      <Card>
        <CardHeader><CardTitle className="text-base">Delivery Funnel</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {funnel.map((step, idx) => {
              const totalSent = funnel[0].value
              const pctOfSent = totalSent > 0 ? (step.value / totalSent) * 100 : 0
              const prev = idx > 0 ? funnel[idx - 1].value : null
              const dropOff = prev && prev > 0 ? ((prev - step.value) / prev) * 100 : null
              return (
                <div key={step.label} className="text-center">
                  <p className="text-xs text-muted-foreground h-4">
                    {idx > 0 && dropOff !== null && dropOff >= 0.1 ? `↓ ${dropOff.toFixed(1)}% drop-off` : ''}
                  </p>
                  <p className="text-2xl font-semibold">{step.value}</p>
                  <p className="text-sm text-muted-foreground">{step.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{pctOfSent.toFixed(1)}% of sent</p>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Engagement Over Time</CardTitle></CardHeader>
        <CardContent>
          {seriesLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : series.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">No engagement data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={series} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="delivered" name="Delivered" fill={METRIC_COLORS.delivered} radius={[3, 3, 0, 0]} />
                <Bar dataKey="opened" name="Opened" fill={METRIC_COLORS.opened} radius={[3, 3, 0, 0]} />
                <Bar dataKey="clicked" name="Clicked" fill={METRIC_COLORS.clicked} radius={[3, 3, 0, 0]} />
                <Bar dataKey="bounced" name="Bounced" fill={METRIC_COLORS.bounced} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

const RECIPIENT_STATUS_TABS = ['all', 'delivered', 'opened', 'clicked', 'bounced', 'unsubscribed', 'complained', 'failed']

export const RecipientsTab = ({ campaignId }) => {
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery(
    ['campaign-recipients', campaignId, status, page],
    () => emailCampaignService.getRecipients(campaignId, { status, page, limit: 20 })
  )
  const recipients = data?.data?.recipients || []
  const pagination = data?.data?.pagination

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1">
        {RECIPIENT_STATUS_TABS.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={status === s ? 'secondary' : 'ghost'}
            onClick={() => { setStatus(s); setPage(1) }}
            className="capitalize"
          >
            {s}
          </Button>
        ))}
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="bg-muted/50 font-medium">Email</TableHead>
                <TableHead className="bg-muted/50 font-medium">Name</TableHead>
                <TableHead className="bg-muted/50 font-medium">Status</TableHead>
                <TableHead className="bg-muted/50 font-medium">Sent</TableHead>
                <TableHead className="bg-muted/50 font-medium">Opened</TableHead>
                <TableHead className="bg-muted/50 font-medium">Clicked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>{[...Array(6)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                ))
              ) : recipients.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="h-24 text-center text-muted-foreground">No recipients found.</TableCell></TableRow>
              ) : (
                recipients.map((r) => (
                  <TableRow key={r.id} className="border-border">
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{[r.contact?.first_name, r.contact?.last_name].filter(Boolean).join(' ') || '—'}</TableCell>
                    <TableCell><Badge variant="secondary">{r.status}</Badge></TableCell>
                    <TableCell>{r.sent_at ? formatDateTime(r.sent_at) : '—'}</TableCell>
                    <TableCell>{r.opened_at ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{r.clicked_at ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Page {pagination.page} of {pagination.totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  )
}

export const LinksTab = ({ campaignId }) => {
  const [expandedUrl, setExpandedUrl] = useState(null)
  const { data, isLoading } = useQuery(['campaign-links', campaignId], () => emailCampaignService.getLinks(campaignId))
  const links = data?.data?.links || []

  const linkLabel = (url) => (url === '{{unsubscribe_url}}' || url.includes('email-unsubscribe') ? 'Unsubscribe' : url)

  if (isLoading) return <Skeleton className="h-64 w-full" />

  if (links.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          No link clicks yet.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="bg-muted/50 font-medium">Link</TableHead>
              <TableHead className="bg-muted/50 font-medium">Total Clicks</TableHead>
              <TableHead className="bg-muted/50 font-medium">Unique Clicks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.map((link) => {
              const isExpanded = expandedUrl === link.url
              return (
                <React.Fragment key={link.url}>
                  <TableRow
                    className="border-border cursor-pointer"
                    onClick={() => setExpandedUrl(isExpanded ? null : link.url)}
                  >
                    <TableCell className="max-w-md truncate">{linkLabel(link.url)}</TableCell>
                    <TableCell>{link.clicks}</TableCell>
                    <TableCell>{link.uniqueClicks}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="border-border hover:bg-transparent">
                      <TableCell colSpan={3} className="bg-muted/30 p-0">
                        {link.clickDetails?.length ? (
                          <Table>
                            <TableHeader>
                              <TableRow className="hover:bg-transparent border-border">
                                <TableHead className="font-medium">Name</TableHead>
                                <TableHead className="font-medium">Email</TableHead>
                                <TableHead className="font-medium">Times Clicked</TableHead>
                                <TableHead className="font-medium">Last Clicked At</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {link.clickDetails.map((c, i) => (
                                <TableRow key={i} className="border-border">
                                  <TableCell>{[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                                  <TableCell>{c.email}</TableCell>
                                  <TableCell>{c.clickCount}</TableCell>
                                  <TableCell>{formatDateTime(c.lastClickedAt)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <p className="p-4 text-sm text-muted-foreground">No click details available.</p>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

const BreakdownBars = ({ title, counts }) => {
  const entries = Object.entries(counts || {}).filter(([, v]) => v > 0)
  const total = entries.reduce((sum, [, v]) => sum + v, 0)

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No data yet.</p>
        ) : (
          entries.map(([label, value]) => {
            const pct = total > 0 ? Math.round((value / total) * 100) : 0
            return (
              <div key={label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="capitalize text-muted-foreground">{label}</span>
                  <span className="font-medium">{value} ({pct}%)</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: '#2a78d6' }} />
                </div>
              </div>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}

// Prefer the frozen snapshot taken at send time (so this reflects exactly what recipients
// got, even if the source template has since been edited); fall back to the live template
// for campaigns that haven't sent yet (DRAFT/SCHEDULED).
export const EmailContentTab = ({ campaign }) => {
  const snapshot = campaign.template_snapshot
  const subject = snapshot?.subject_line || campaign.template?.subject_line
  const previewText = snapshot?.preview_text || campaign.template?.preview_text
  const html = snapshot?.html_body || campaign.template?.html_body
  const fromName = snapshot?.from_name || campaign.from_name
  const fromEmail = snapshot?.from_email || campaign.from_email

  if (!html) {
    return (
      <Card>
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          This campaign does not have any email content to preview.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {(fromName || fromEmail) && (
        <Card>
          <CardContent className="p-4 text-sm">
            <span className="text-muted-foreground mr-2">From:</span>
            <span className="font-medium">
              {fromName}{fromName && fromEmail ? ' ' : ''}{fromEmail ? `<${fromEmail}>` : ''}
            </span>
          </CardContent>
        </Card>
      )}
      <EmailPreview subject={subject} previewText={previewText} html={html} />
    </div>
  )
}

export const DeliverabilityTab = ({ campaignId }) => {
  const { data: devicesData, isLoading: devicesLoading } = useQuery(
    ['campaign-devices', campaignId], () => emailCampaignService.getDevices(campaignId)
  )
  const { data: providersData, isLoading: providersLoading } = useQuery(
    ['campaign-providers', campaignId], () => emailCampaignService.getProviders(campaignId)
  )

  if (devicesLoading || providersLoading) return <Skeleton className="h-64 w-full" />

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <BreakdownBars title="Device Breakdown" counts={devicesData?.data} />
      <BreakdownBars title="Email Provider Breakdown" counts={providersData?.data} />
    </div>
  )
}
